/**
 * Editing where an application runs, and which devices a tester must have.
 *
 * RidgeWill/platform#56 (2026-10-20). Jon asked for the platforms to stay on
 * the Settings tab if they could, behind an "Edit platforms" button, with
 * AND/OR logic over the grouping. The Settings field now shows the result as
 * chips and one sentence; everything that takes thought happens here.
 *
 * Two halves, because they are two facts. "Runs on" is what the app supports
 * (the column every other screen reads). "A tester needs" is the gate on the
 * tester card, built from those platforms but not always equal to them: Keyodo
 * runs on the web too, and a PC alone still should not get a tester in.
 */
import { useMemo, useState, type ReactNode } from 'react';
import { C } from '@ridgewill/brand';
import { Modal, ModalActions } from '../components/Modal.js';
import { Menu, MenuItem, PrimaryButton } from '../components/Screen.js';
import { Badge, Button, PlatformGrid } from '../components/ui.js';
import { PLATFORMS, platformLabel } from '../lib/platforms.js';
import {
  defaultRequirement,
  describeRequirement,
  meetsRequirement,
  platformsOf,
  strayPlatforms,
  upgradeRequirement,
  type Requirement,
  type RequirementGroup,
} from '../lib/tester-requirements.js';

/** Who the preview tries the rule against: one device each, the common cases. */
const SAMPLES = [
  { label: 'Only an iPhone', devices: [{ type: 'phone', os: 'ios' }] },
  { label: 'Only an Android phone', devices: [{ type: 'phone', os: 'android' }] },
  { label: 'Only an iPad', devices: [{ type: 'tablet', os: 'ipados' }] },
  { label: 'Only an Android tablet', devices: [{ type: 'tablet', os: 'android' }] },
  { label: 'Only a Windows PC', devices: [{ type: 'computer', os: 'windows' }] },
  { label: 'Only a Mac', devices: [{ type: 'computer', os: 'macos' }] },
  { label: 'An iPhone and a Mac', devices: [{ type: 'phone', os: 'ios' }, { type: 'computer', os: 'macos' }] },
].map((x) => ({ label: x.label, has: x.devices.flatMap(platformsOf) }));

export function EditPlatforms({
  appName,
  platforms: initialPlatforms,
  requirement: initialRequirement,
  onClose,
  onSave,
}: {
  appName: string;
  platforms: string[];
  requirement: Requirement | null;
  onClose: () => void;
  onSave: (platforms: string[], requirement: Requirement) => void;
}) {
  const [platforms, setPlatforms] = useState(initialPlatforms);
  const [requirement, setRequirement] = useState<Requirement>(
    () => (initialRequirement ? upgradeRequirement(initialRequirement) : defaultRequirement(initialPlatforms)),
  );
  /* Until somebody touches the groups, they follow the platforms: ticking iOS
     adds it to the default rule. The first edit to a group makes it theirs. */
  const [custom, setCustom] = useState(initialRequirement !== null);
  const rule = custom ? requirement : defaultRequirement(platforms);
  const stray = strayPlatforms(rule, platforms);

  const edit = (next: RequirementGroup[]) => {
    setCustom(true);
    setRequirement({ groups: next });
  };
  const groups = rule.groups;
  const setGroup = (i: number, g: RequirementGroup) => edit(groups.map((x, j) => (j === i ? g : x)));

  const sentence = useMemo(() => describeRequirement(rule), [rule]);

  return (
    <Modal
      title="Platforms"
      subtitle={`Where ${appName} runs, and which devices a person needs before they can sign up to test it.`}
      width={640}
      onClose={onClose}
      footer={
        <ModalActions>
          <Button onClick={onClose}>Cancel</Button>
          <PrimaryButton onClick={() => onSave(platforms, rule)} disabled={platforms.length === 0}>
            Save platforms
          </PrimaryButton>
        </ModalActions>
      }
    >
      <section style={s.section}>
        <h3 style={s.h}>Runs on</h3>
        <PlatformGrid picked={platforms} onChange={setPlatforms} />
        {platforms.length === 0 ? <span style={s.warn}>Add at least one platform.</span> : null}
      </section>

      <section style={s.section}>
        <div style={s.hRow}>
          <h3 style={s.h}>A tester needs</h3>
          {custom ? (
            <button type="button" style={s.link} onClick={() => setCustom(false)}>
              Reset to match the platforms
            </button>
          ) : (
            <span style={s.mutedSmall}>Following the platforms until you change it</span>
          )}
        </div>

        <div style={{ display: 'grid' }}>
          {groups.map((g, i) => (
            <div key={i}>
              {i > 0 ? (
                <div style={s.joiner} aria-hidden>
                  <span style={s.joinerLine} />
                  <span style={s.joinerPill}>AND</span>
                  <span style={s.joinerLine} />
                </div>
              ) : null}
              <GroupBox
                group={g}
                index={i}
                platforms={platforms}
                onChange={(next) => setGroup(i, next)}
                onRemove={groups.length > 1 ? () => edit(groups.filter((_, j) => j !== i)) : undefined}
              />
            </div>
          ))}
        </div>

        <div>
          <Button onClick={() => edit([...groups, { mode: 'any', platforms: [] }])}>+ Add another requirement (AND)</Button>
        </div>

        {stray.length ? (
          <span style={s.warn}>
            {stray.map(platformLabel).join(', ')} {stray.length === 1 ? 'is' : 'are'} required but not in Runs on. Add{' '}
            {stray.length === 1 ? 'it' : 'them'} above, or take {stray.length === 1 ? 'it' : 'them'} out of the requirement.
          </span>
        ) : null}
      </section>

      <section style={s.preview} aria-live="polite">
        <div style={{ fontWeight: 650, color: C.text, fontSize: '.88rem' }}>{sentence}</div>
        <table style={s.sampleTable}>
          <tbody>
            {SAMPLES.map((x) => {
              const ok = meetsRequirement(rule, x.has);
              return (
                <tr key={x.label}>
                  <td style={s.sampleCell}>{x.label}</td>
                  <td style={{ ...s.sampleCell, textAlign: 'right' }}>
                    <Badge tone={ok ? 'good' : 'neutral'}>{ok ? 'Can sign up' : 'Cannot sign up'}</Badge>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>
    </Modal>
  );
}

function GroupBox({
  group,
  index,
  platforms,
  onChange,
  onRemove,
}: {
  group: RequirementGroup;
  index: number;
  platforms: string[];
  onChange: (g: RequirementGroup) => void;
  onRemove?: () => void;
}) {
  const addable = PLATFORMS.filter((p) => !group.platforms.includes(p.value));
  const word = group.mode === 'all' ? 'and' : 'or';
  return (
    <div style={s.group}>
      <div style={s.groupHead}>
        <span style={s.groupTitle}>Requirement {index + 1}</span>
        <span role="radiogroup" aria-label={`How requirement ${index + 1} is met`} style={s.seg}>
          {(['any', 'all'] as const).map((m) => (
            <button
              key={m}
              type="button"
              role="radio"
              aria-checked={group.mode === m}
              onClick={() => onChange({ ...group, mode: m })}
              style={{ ...s.segBtn, ...(group.mode === m ? s.segOn : {}) }}
            >
              {m === 'any' ? 'Any one of' : 'All of'}
            </button>
          ))}
        </span>
        {onRemove ? (
          <button type="button" style={{ ...s.link, marginLeft: 'auto', color: C.dangerText }} onClick={onRemove}>
            Remove
          </button>
        ) : null}
      </div>
      <div style={s.chipRow}>
        {group.platforms.length === 0 ? <span style={s.mutedSmall}>No platforms yet.</span> : null}
        {group.platforms.map((p, i) => (
          <span key={p} style={{ display: 'inline-flex', alignItems: 'center', gap: '.4rem' }}>
            {i > 0 ? <span style={s.word}>{word}</span> : null}
            <Chip tone={platforms.includes(p) ? undefined : 'warn'} onRemove={() => onChange({ ...group, platforms: group.platforms.filter((x) => x !== p) })}>
              {platformLabel(p)}
            </Chip>
          </span>
        ))}
        {addable.length ? (
          <Menu label={`Add a platform to requirement ${index + 1}`} align="left" trigger={<span style={s.addTrigger}>+ {group.platforms.length ? word : 'Add'}</span>}>
            {(close) =>
              addable.map((p) => (
                <MenuItem
                  key={p.value}
                  onClick={() => {
                    onChange({ ...group, platforms: [...group.platforms, p.value] });
                    close();
                  }}
                >
                  {p.label}
                  {platforms.includes(p.value) ? '' : ' (not in Runs on)'}
                </MenuItem>
              ))
            }
          </Menu>
        ) : null}
      </div>
    </div>
  );
}

/** A platform as a removable chip: the same chip the Settings field shows. */
export function Chip({ children, onRemove, tone }: { children: ReactNode; onRemove?: () => void; tone?: 'warn' }) {
  return (
    <span style={{ ...s.chip, ...(tone === 'warn' ? s.chipWarn : {}) }}>
      {children}
      {onRemove ? (
        <button type="button" onClick={onRemove} aria-label={`Remove ${String(children)}`} style={s.chipX}>
          ✕
        </button>
      ) : null}
    </span>
  );
}

const s = {
  section: { display: 'grid', gap: '.6rem' },
  h: { margin: 0, fontSize: '.85rem', fontWeight: 650, color: C.textMid },
  hRow: { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '1rem' },
  chipRow: { display: 'flex', alignItems: 'center', gap: '.45rem', flexWrap: 'wrap' as const },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '.35rem',
    padding: '.22rem .35rem .22rem .6rem',
    borderRadius: 999,
    fontSize: '.82rem',
    fontWeight: 600,
    background: C.blueTint,
    color: C.blueText,
    border: `1px solid ${C.border2}`,
  },
  chipWarn: { background: C.warnBg, color: C.warnText },
  chipX: {
    border: 'none',
    background: 'transparent',
    color: 'inherit',
    cursor: 'pointer',
    fontSize: '.7rem',
    padding: '.1rem .25rem',
    borderRadius: 999,
    opacity: 0.75,
  },
  addTrigger: { fontSize: '.82rem', fontWeight: 600, color: C.brand, padding: '.22rem .5rem', border: `1px dashed ${C.inputBorder}`, borderRadius: 999 },
  word: { fontSize: '.72rem', fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase' as const, color: C.muted },
  group: { border: `1px solid ${C.border}`, borderRadius: 8, padding: '.75rem .85rem', display: 'grid', gap: '.6rem', background: C.surface },
  groupHead: { display: 'flex', alignItems: 'center', gap: '.75rem' },
  groupTitle: { fontSize: '.8rem', fontWeight: 650, color: C.textMid },
  seg: { display: 'inline-flex', border: `1px solid ${C.inputBorder}`, borderRadius: 6, overflow: 'hidden' },
  segBtn: { border: 'none', background: 'transparent', color: C.textMid, fontSize: '.78rem', fontWeight: 600, padding: '.2rem .55rem', cursor: 'pointer' },
  segOn: { background: C.blueTint, color: C.blueText },
  joiner: { display: 'flex', alignItems: 'center', gap: '.5rem', margin: '.35rem 0' },
  joinerLine: { flex: 1, height: 1, background: C.border },
  joinerPill: { fontSize: '.7rem', fontWeight: 750, letterSpacing: '.08em', color: C.textMid, border: `1px solid ${C.border2}`, borderRadius: 999, padding: '.08rem .5rem', background: C.page },
  link: { border: 'none', background: 'none', padding: 0, color: C.brand, fontSize: '.8rem', fontWeight: 600, cursor: 'pointer' },
  mutedSmall: { fontSize: '.78rem', color: C.muted },
  warn: { fontSize: '.78rem', color: C.warnText, lineHeight: 1.4 },
  preview: { display: 'grid', gap: '.5rem', background: C.page, border: `1px solid ${C.border}`, borderRadius: 8, padding: '.8rem .9rem' },
  sampleTable: { width: '100%', borderCollapse: 'collapse' as const, fontSize: '.82rem' },
  sampleCell: { padding: '.28rem 0', borderTop: `1px solid ${C.border}`, color: C.textMid },
};
